import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import './SignUp.css';

function SignUp() {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [name, setName] = useState('');

    useEffect(() => {
        const checkUser = async () => {
            const { data: { user } } = await supabase.auth.getUser();
            if (user) {
                const uiLanguage = localStorage.getItem('vozoly_ui_language') || '';
                const targetLanguage = localStorage.getItem('vozoly_target_language') || '';
                const goals = JSON.parse(localStorage.getItem('vozoly_goals') || '[]');
                const level = localStorage.getItem('vozoly_level') || 'A1';
                const placementPath = localStorage.getItem('vozoly_placement_path') || '';

                if (targetLanguage) {
                    await supabase
                        .from('profiles')
                        .upsert({
                            id: user.id,
                            ui_language: uiLanguage,
                            target_language: targetLanguage,
                            goals: goals,
                            level: level,
                            placement_path: placementPath,
                            display_name: localStorage.getItem('vozoly_display_name') || '',
                        });

                    localStorage.removeItem('vozoly_level');
                    localStorage.removeItem('vozoly_target_language');
                    localStorage.removeItem('vozoly_ui_language');
                    localStorage.removeItem('vozoly_goals');
                    localStorage.removeItem('vozoly_placement_path');
                    localStorage.removeItem('vozoly_onboarding_step');
                }

                navigate('/home');
            }
        };

        checkUser();
    }, [navigate]);

    const level = localStorage.getItem('vozoly_level') || 'A1';
    const targetLanguage = localStorage.getItem('vozoly_target_language') || '';
    const uiLanguage = localStorage.getItem('vozoly_ui_language') || '';
    const goals = JSON.parse(localStorage.getItem('vozoly_goals') || '[]');
    const placementPath = localStorage.getItem('vozoly_placement_path') || '';

    const handleSignUp = async () => {
        setError('');

        if (!name || !email || !password || !confirmPassword) {
            setError('Please fill in all fields.');
            return;
        }

        if (password !== confirmPassword) {
            setError('Passwords do not match.');
            return;
        }

        if (password.length < 8) {
            setError('Password must be at least 8 characters.');
            return;
        }

        setLoading(true);

        try {
            const { data, error: signUpError } = await supabase.auth.signUp({
                email,
                password,
            });

            if (signUpError) {
                setError(signUpError.message);
                setLoading(false);
                return;
            }

            if (data.user) {
                const { error: profileError } = await supabase
                    .from('profiles')
                    .insert({
                        id: data.user.id,
                        ui_language: uiLanguage,
                        target_language: targetLanguage,
                        goals: goals,
                        level: level,
                        placement_path: placementPath,
                        display_name: name,
                    });

                if (profileError) {
                    console.error('Profile error:', profileError);
                }

                localStorage.removeItem('vozoly_level');
                localStorage.removeItem('vozoly_target_language');
                localStorage.removeItem('vozoly_ui_language');
                localStorage.removeItem('vozoly_goals');
                localStorage.removeItem('vozoly_placement_path');
                localStorage.removeItem('vozoly_onboarding_step');

                navigate('/home');
            }
        } catch (err) {
            setError('Something went wrong. Please try again.');
        }

        setLoading(false);
    };

    return (
        <div className="su-screen">

            <div className="su-top">
                <button className="su-back" onClick={() => navigate(-1)}>←</button>
            </div>

            <div className="su-content">

                <div className="su-level-banner">
                    <div className="su-level-circle">{level}</div>
                    <div className="su-level-info">
                        <div className="su-level-title">Your level is saved</div>
                        <div className="su-level-sub">
                            Create your account to start learning {targetLanguage} at {level}
                        </div>
                    </div>
                </div>

                <div className="su-heading">Create your account</div>
                <div className="su-subheading">
                    Free to start — no credit card needed.
                </div>

                <div className="su-form">

                    <div className="su-field">
                        <div className="su-label">Your first name</div>
                        <input
                            className="su-input"
                            type="text"
                            placeholder="What should Gabi call you?"
                            value={name}
                            onChange={e => setName(e.target.value)}
                            autoCapitalize="words"
                        />
                    </div>

                    <div className="su-field">
                        <div className="su-label">Email address</div>
                        <input
                            className="su-input"
                            type="email"
                            placeholder="you@example.com"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            autoCapitalize="none"
                        />
                    </div>

                    <div className="su-field">
                        <div className="su-label">Password</div>
                        <div className="su-input-wrap">
                            <input
                                className="su-input su-input-pw"
                                type={showPassword ? 'text' : 'password'}
                                placeholder="At least 8 characters"
                                value={password}
                                onChange={e => setPassword(e.target.value)}
                            />
                            <button
                                className="su-show-pw"
                                onClick={() => setShowPassword(!showPassword)}
                            >
                                {showPassword ? 'Hide' : 'Show'}
                            </button>
                        </div>
                    </div>

                    <div className="su-field">
                        <div className="su-label">Confirm password</div>
                        <input
                            className="su-input"
                            type={showPassword ? 'text' : 'password'}
                            placeholder="Repeat your password"
                            value={confirmPassword}
                            onChange={e => setConfirmPassword(e.target.value)}
                        />
                    </div>

                    {error && (
                        <div className="su-error">
                            ⚠️ {error}
                        </div>
                    )}

                    <button
                        className="su-btn-primary"
                        onClick={handleSignUp}
                        disabled={loading}
                    >
                        {loading ? 'Creating account...' : 'Create free account →'}
                    </button>

                    <div className="su-divider">
                        <div className="su-divider-line"></div>
                        <div className="su-divider-text">or</div>
                        <div className="su-divider-line"></div>
                    </div>

                    <button
                        className="su-btn-google"
                        disabled
                    >
                        <span className="su-google-icon">G</span>
                        Continue with Google
                        <span className="su-coming-soon">Coming soon</span>
                    </button>

                </div>

                <div className="su-signin-link">
                    Already have an account?{' '}
                    <span className="su-signin-btn" onClick={() => navigate('/signin')}>
                        Sign in
                    </span>
                </div>

                <div className="su-terms">
                    By creating an account you agree to our Terms of Service and Privacy Policy.
                </div>

            </div>

        </div>
    );
}

export default SignUp;